import React, { useState, useEffect } from "react";
import { io } from "socket.io-client";
import "./admincontrols.scss";

const SOCKET_URL = "https://coolseraback.onrender.com";

function AdminControls() {
  const [socket, setSocket] = useState(null);
  const [roomname, setRoomname] = useState("");
  const [bpm, setBpm] = useState(120);
  const [timeSignature, setTimeSignature] = useState("4/4");
  const [numColors, setNumColors] = useState(1);
  const [colors, setColors] = useState(["#ff0000"]);
  const [effectActive, setEffectActive] = useState(false);
  const [extraFeaturesEnabled, setExtraFeaturesEnabled] = useState(false);

  // Estado de autenticación
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Lista de salas
  const rooms = ["Izquierda", "Centro", "Derecha"];
  const [roomMessages, setRoomMessages] = useState({});
  const [roomConfigs, setRoomConfigs] = useState({});

  // Inicializar el socket
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      transports: ["websocket"],
      withCredentials: true,
    });

    setSocket(newSocket);

    newSocket.on("message", (message) => {
      setRoomMessages((prev) => ({
        ...prev,
        [message.room]: message,
      }));
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  // Ajustar colores según el número de colores
  useEffect(() => {
    if (numColors < colors.length) {
      setColors(colors.slice(0, numColors));
    } else if (numColors > colors.length) {
      setColors([...colors, ...Array(numColors - colors.length).fill("#ff0000")]);
    }
  }, [numColors]);

  const sendData = (room) => {
    if (!socket) return;
    const message = { numColors, colors, bpm, timeSignature, effectActive, roomname: room };

    socket.emit("adminSend", message);

    setRoomConfigs((prev) => ({
      ...prev,
      [room]: {
        bpm,
        timeSignature,
        colors,
        effectActive,
      },
    }));

    setRoomMessages((prev) => ({
      ...prev,
      [room]: {
        userId: "Admin",
        username: "Admin",
        message: `Configuración enviada: BPM: ${bpm}, Compás: ${timeSignature}, Colores: ${colors.join(", ")}`,
        room: room,
      },
    }));
  };

  const sendToAllRooms = () => {
    rooms.forEach(sendData);
  };

  const handleLogin = async () => {
    try {
      const response = await fetch(`${SOCKET_URL}/admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        setIsLoggedIn(true);
        setLoginError("");
      } else {
        setLoginError(data.message);
      }
    } catch (error) {
      setLoginError("Error de red. Intenta nuevamente.");
    }
  };

  const handleColorSelection = (color, index) => {
    const newColors = [...colors];
    newColors[index] = color;
    setColors(newColors);

    if (roomname) {
      setRoomConfigs((prev) => ({
        ...prev,
        [roomname]: {
          ...prev[roomname],
          colors: newColors,
        },
      }));
    }
  };

  const handleCheckboxChange = () => {
    setExtraFeaturesEnabled(!extraFeaturesEnabled);
  };

  if (!isLoggedIn) {
    return (
      <div className="login-container">
        <h1>Inicio de Sesión Administrador</h1>
        <input
          type="text"
          placeholder="Usuario"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="password"
          placeholder="Contraseña"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button onClick={handleLogin}>Iniciar Sesión</button>
        {loginError && <p className="error">{loginError}</p>}
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h1>Admin Panel</h1>

      <div className="button-container">
        <button onClick={() => setEffectActive(!effectActive)}>
          {effectActive ? "Desactivar Efecto" : "Activar Efecto"}
        </button>
        <button onClick={() => sendData(roomname)} disabled={!roomname}>
          Enviar a Sala
        </button>
        <button onClick={sendToAllRooms}>Enviar a Todas las Salas</button>
      </div>

      <h2>Vista Previa de Salas</h2>
      <div className="room-previews">
        {rooms.map((room, index) => (
          <div
            key={index}
            className={`room-preview ${roomname === room ? "selected-room" : ""} ${
              roomConfigs[room]?.effectActive ? "effect-active" : ""
            }`}
            onClick={() => setRoomname(room)}
          >
            <h3>{room}</h3>
            <p><strong>BPM:</strong> {roomConfigs[room]?.bpm || "NADA"}</p>
            <p><strong>Compás:</strong> {roomConfigs[room]?.timeSignature || "NADA"}</p>
            <p><strong>Colores:</strong> 
              {roomConfigs[room]?.colors?.length > 0 
                ? roomConfigs[room].colors.map((color, index) => (
                    <span 
                      key={index} 
                      className="color-swatch" 
                      style={{
                        backgroundColor: color,
                        marginRight: "5px",
                        padding: "5px",
                        borderRadius: "50%",
                        width: "25px", 
                        height: "25px",
                        display: "inline-block",
                      }}
                    />
                  ))
                : "No configurado"}
            </p>
            <p>
              <strong>Efecto Activo:</strong> 
              <span 
                className={`effect-status ${
                  roomConfigs[room]?.effectActive ? "active" : "inactive"
                }`}
              >
                {roomConfigs[room]?.effectActive ? "Sí" : "No"}
              </span>
            </p>
          </div>
        ))}
      </div>

      <div className="color-settings">
        <label htmlFor="num-colors">Número de Colores:</label>
        <select 
          id="num-colors"
          value={numColors}
          onChange={(e) => setNumColors(Number(e.target.value))}
        >
          {[...Array(8).keys()].map((num) => (
            <option key={num} value={num + 1}>{num + 1}</option>
          ))}
        </select>
      </div>

      <div className="color-boxes">
        {[...Array(numColors)].map((_, rowIndex) => (
          <div key={rowIndex} className="color-row">
            {["#ff0000", "#00ff00", "#0000ff", "#ffff00", "#ff00ff", "#00ffff", "#ffffff", "#000000"].map((color, colIndex) => (
              <div 
                key={colIndex} 
                className={`color-box ${colors[rowIndex] === color ? "selected" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorSelection(color, rowIndex)}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="bpm-settings">
              <label>BPM:</label>
              <input
                type="number"
                value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
              />
            </div>
    </div>
  );
}

export default AdminControls;
